import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Compare from "./pages/Compare";
import History from "./pages/History";
import Alerts from "./pages/Alerts";
import Electoral360 from "./pages/Electoral360";
import FiqueDeOlho from "./pages/FiqueDeOlho";
import Transparency from "./pages/Transparency";
import Elections from "./pages/Elections";
import RadarEleitoral from "./pages/RadarEleitoral";
import SharedAnalysis from "./pages/SharedAnalysis";
import PublicCandidate from "./pages/PublicCandidate";
import PublicCompare from "./pages/PublicCompare";
import PublicAlertas from "./pages/PublicAlertas";
import DinheiroPublico from "./pages/DinheiroPublico";
import ComingSoon from "./pages/ComingSoon";
import MeuMunicipio from "./pages/MeuMunicipio";
import MeuPolitico from "./pages/MeuPolitico";
import EmpresaBusca from "./pages/EmpresaBusca";
import Empresa from "./pages/Empresa";
import Emendas from "./pages/Emendas";
import EmendasParlamentar from "./pages/EmendasParlamentar";
import EmendasMunicipio from "./pages/EmendasMunicipio";
import CenarioEleitoral from "./pages/CenarioEleitoral";
import PesquisaDetalhe from "./pages/PesquisaDetalhe";
import Perfil from "./pages/Perfil";
import BrasilTransparente from "./pages/BrasilTransparente";
import BrasilTransparenteMunicipio from "./pages/BrasilTransparenteMunicipio";
import BrasilTransparenteDeputado from "./pages/BrasilTransparenteDeputado";
import BrasilTransparenteEmpresa from "./pages/BrasilTransparenteEmpresa";
import Privacy from "./pages/Privacy";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import ResetPassword from "./pages/ResetPassword";
import VerifyRecoveryEmail from "./pages/VerifyRecoveryEmail";
import Plans from "./pages/Plans";
import Checkout from "./pages/Checkout";
import { TermsOfUse, CancellationPolicy } from "./pages/LegalPages";
import ErrorBoundary from "./components/ErrorBoundary";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";

// Admin Panel (P0) — fully isolated route tree at /admin/*
import { AdminAuthProvider, useAdminAuth } from "./admin/AdminAuthContext";
import AdminLogin from "./admin/AdminLogin";
import AdminAuthCallback from "./admin/AdminAuthCallback";
import AdminLayout from "./admin/AdminLayout";
import AdminChangePassword from "./admin/AdminChangePassword";
import AdminForgotPassword from "./admin/AdminForgotPassword";
import AdminResetPassword from "./admin/AdminResetPassword";
import AdminProfile from "./admin/AdminProfile";
import AdminVerifyRecoveryEmail from "./admin/AdminVerifyRecoveryEmail";
import AdminDashboard from "./admin/AdminDashboard";
import AdminAdmins from "./admin/AdminAdmins";
import AdminAudit from "./admin/AdminAudit";
import AdminTransactions from "./admin/AdminTransactions";
import AdminUsers from "./admin/AdminUsers";
import AdminUsage from "./admin/AdminUsage";
import AdminAlerts from "./admin/AdminAlerts";
import { Navigate } from "react-router-dom";

const AdminProtected = ({ children, superOnly = false }) => {
  const { admin, loading, isSuper } = useAdminAuth();
  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;
  if (superOnly && !isSuper) return <Navigate to="/admin/dashboard" replace />;
  return children;
};

const AdminApp = () => (
  <AdminAuthProvider>
    <Routes>
      <Route path="login" element={<AdminLogin />} />
      <Route path="auth/callback" element={<AdminAuthCallback />} />
      <Route path="forgot-password" element={<AdminForgotPassword />} />
      <Route path="reset-password" element={<AdminResetPassword />} />
      <Route path="verify-recovery-email" element={<AdminVerifyRecoveryEmail />} />
      <Route path="change-password" element={
        <AdminProtected><AdminChangePassword /></AdminProtected>
      } />
      <Route path="" element={
        <AdminProtected><AdminLayout /></AdminProtected>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="usage" element={<AdminUsage />} />
        <Route path="alerts" element={<AdminAlerts />} />
        <Route path="profile" element={<AdminProfile />} />
        <Route path="transactions" element={<AdminTransactions />} />
        <Route path="admins" element={
          <AdminProtected superOnly><AdminAdmins /></AdminProtected>
        } />
        <Route path="audit" element={
          <AdminProtected superOnly><AdminAudit /></AdminProtected>
        } />
      </Route>
    </Routes>
  </AdminAuthProvider>
);

function App() {
  // Captura ?ref=CODE em qualquer entrada do site e persiste ate o cadastro consumir
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref && /^[A-Z0-9]{4,12}$/.test(ref.toUpperCase())) {
        localStorage.setItem('tdo_ref', ref.toUpperCase());
      }
    } catch (_) { /* silencioso */ }
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
      <div className="App bg-[#F4F6F8] min-h-screen">
        <BrowserRouter>
          {/* Admin panel — completely isolated from the user-facing AuthProvider */}
          <Routes>
            <Route path="/admin/*" element={<AdminApp />} />
            <Route path="*" element={
              <AuthProvider>
                <Routes>
                  {/* Public routes - no layout */}
                  <Route path="/share/:shareId" element={<SharedAnalysis />} />
                  <Route path="/candidato/:slug" element={<PublicCandidate />} />
                  <Route path="/comparar" element={<PublicCompare />} />
                  <Route path="/privacidade" element={<Privacy />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/verify-recovery-email" element={<VerifyRecoveryEmail />} />
                  <Route path="/termos" element={<TermsOfUse />} />
                  <Route path="/cancelamento" element={<CancellationPolicy />} />
                  {/* Main app routes with sidebar layout */}
                  <Route path="*" element={
                    <Layout>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/compare" element={<Compare />} />
                        <Route path="/history" element={<History />} />
                        <Route path="/alerts" element={<Alerts />} />
                        <Route path="/monitor" element={<Electoral360 />} />
                        <Route path="/fique-de-olho" element={<FiqueDeOlho />} />
                        <Route path="/transparencia" element={<Transparency />} />
                        <Route path="/eleicoes" element={<Elections />} />
                        <Route path="/radar" element={<RadarEleitoral />} />
                        <Route path="/planos" element={<Plans />} />
                        <Route path="/checkout/:planId" element={<Checkout />} />
                        {/* Fase 2 — real pages (were stubs on iter 30) */}
                        <Route path="/meu-municipio" element={<MeuMunicipio />} />
                        <Route path="/meu-politico" element={<MeuPolitico />} />
                        {/* Fase 3 — real pages */}
                        <Route path="/dinheiro-publico" element={<DinheiroPublico />} />
                        <Route path="/alertas" element={<PublicAlertas />} />
                        <Route path="/empresa" element={<EmpresaBusca />} />
                        <Route path="/empresa/:cnpj" element={<Empresa />} />
                        <Route path="/emendas" element={<Emendas />} />
                        <Route path="/emendas/parlamentar/:pid" element={<EmendasParlamentar />} />
                        <Route path="/emendas/municipio/:cod_ibge" element={<EmendasMunicipio />} />
                        <Route path="/cenario-eleitoral" element={<CenarioEleitoral />} />
                        <Route path="/pesquisa/:registro" element={<PesquisaDetalhe />} />
                        <Route path="/brasil-transparente" element={<BrasilTransparente />} />
                        <Route path="/brasil-transparente/municipio/:nome" element={<BrasilTransparenteMunicipio />} />
                        <Route path="/brasil-transparente/deputado/:id" element={<BrasilTransparenteDeputado />} />
                        <Route path="/brasil-transparente/empresa/:cnpj" element={<Empresa />} />
                        <Route path="/perfil" element={<Perfil />} />
                      </Routes>
                    </Layout>
                  } />
                </Routes>
              </AuthProvider>
            } />
          </Routes>
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </div>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
